import React from "react";
import { Helmet } from "react-helmet-async";
import { useContext } from "react";
import { LanguageContext } from "./LanguageContext";

const DOMAIN = "https://homewalkers.ae";

/**
 * SEO component — يتحط جوه أي Page component (MasarPage / EmaarPage)
 * وبيظبط title / description / canonical / OG / Twitter / hreflang
 * ديناميكيًا حسب الصفحة واللغة الحالية.
 *
 * الاستخدام:
 * <SEO
 *   path="/masar"
 *   title={{ ar: "...", en: "..." }}
 *   description={{ ar: "...", en: "..." }}
 *   image="https://homewalkers.ae/masar-og.jpg"
 *   jsonLd={{...}}   // اختياري
 * />
 */
const SEO = ({ path, title, description, image, jsonLd }) => {
  const { language } = useContext(LanguageContext);

  const canonicalUrl = `${DOMAIN}${path}`;
  const arUrl         = `${DOMAIN}${path}?ar`;
  const enUrl          = `${DOMAIN}${path}?en`;

  const pageTitle       = title?.[language]       || title?.ar       || "";
  const pageDescription = description?.[language] || description?.ar || "";

  return (
    <Helmet>
      <html lang={language} dir={language === "ar" ? "rtl" : "ltr"} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />

      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="ar" href={arUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Homewalkers" />
      <meta property="og:locale" content={language === "ar" ? "ar_AE" : "en_US"} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      {image && <meta property="og:image" content={image} />}
      {image && <meta property="og:image:width" content="1200" />}
      {image && <meta property="og:image:height" content="630" />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      {image && <meta name="twitter:image" content={image} />}

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
